import { useState, useEffect, useCallback } from "react";
import Papa from "papaparse";
import { getAllLeathers, getAllLocales, getStrapOptions } from "../components/shop/fetchApi";
import { getCartStorageKey } from "../utils/cartStorage";

const apiURL = (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_URL)
    ? process.env.REACT_APP_API_URL
    : "http://localhost:8000";

const defaultEN = {
    id: 'default-en', code: 'EN', name: 'English',
    iconFile: 'https://cdn-icons-png.flaticon.com/512/197/197374.png'
};

function isFreeMoveBought(leather) {
    try {
        const used = Array.isArray(leather.usedAreas) ? leather.usedAreas : [];
        return used.some(area => area.isFreeMove === true);
    } catch (e) { return false; }
}

export default function useAppData({ i18n }) {
    const [hides, setHides] = useState([]);
    const [strapOptionsList, setStrapOptionsList] = useState([]);
    const [localesList, setLocalesList] = useState([defaultEN]);
    const [currentLocale, setCurrentLocale] = useState(defaultEN);
    const [localCartAreas, setLocalCartAreas] = useState([]);
    const [isLoadingApp, setIsLoadingApp] = useState(true);

    const refreshLeathers = useCallback(async () => {
        try {
            let res = await getAllLeathers();
            if (res && res.Leathers && res.Leathers.length > 0) {
                const availableLeathers = res.Leathers.filter(leather => {
                    const preAreas = Array.isArray(leather.preSelectedAreas) ? leather.preSelectedAreas : [];
                    const allPreSold = preAreas.length > 0 && preAreas.every(a => a.isUsed === true);
                    return !isFreeMoveBought(leather) && leather.isHidden !== true && !allPreSold;
                });
                const allHidden = res.Leathers.every(leather => leather.isHidden === true);
                setHides(allHidden ? [] : availableLeathers);
            } else {
                setHides([]);
            }
        } catch (error) { console.error("refreshLeathers error:", error); }
    }, []);

    // Re-fetch leathers when tab becomes visible (e.g. returning from WooCommerce checkout)
    useEffect(() => {
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') refreshLeathers();
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, [refreshLeathers]);

    // Load local cart from localStorage
    useEffect(() => {
        const STORAGE_KEY = getCartStorageKey();
        const savedCart = localStorage.getItem(STORAGE_KEY);
        if (savedCart) {
            try { setLocalCartAreas(JSON.parse(savedCart)); }
            catch (e) { console.error("Lỗi parse local storage", e); }
        }
    }, []);

    // Listen for cart removal messages from parent domain
    useEffect(() => {
        const allowedOrigins = ["https://handdn.com", "https://handdn.us"];
        const handleCartMessage = (event) => {
            if (!allowedOrigins.includes(event.origin)) return;
            if (event.data && event.data.action === "cart_item_removed") {
                const designId = event.data.designId;
                try {
                    const STORAGE_KEY = getCartStorageKey();
                    let areas = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
                    let foundIndex = areas.findIndex(item => {
                        const compositeId = `${item.hideId}_${Math.round(item.x)}_${Math.round(item.y)}`;
                        return (item.uniqueAreaId && String(item.uniqueAreaId) === String(designId)) ||
                            (compositeId === String(designId)) ||
                            (item.designId && String(item.designId) === String(designId));
                    });
                    if (foundIndex === -1) {
                        foundIndex = areas.findIndex(item => String(item.hideId) === String(designId));
                    }
                    if (foundIndex !== -1) {
                        areas.splice(foundIndex, 1);
                        localStorage.setItem(getCartStorageKey(), JSON.stringify(areas));
                        setLocalCartAreas([...areas]);
                        // Refresh from DB after delay to sync with webhookRemoved result
                        setTimeout(() => refreshLeathers(), 2000);
                    }
                } catch (e) { console.error("Lỗi khi xử lý localStorage cart:", e); }
            }
        };
        window.addEventListener("message", handleCartMessage);
        return () => window.removeEventListener("message", handleCartMessage);
    }, [refreshLeathers]);

    // Fetch leathers + locales + strap options
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                if (typeof getAllLocales === 'function') {
                    let resLocales = await getAllLocales();
                    if (resLocales && resLocales.success && resLocales.locales) {
                        const dbLocales = resLocales.locales;
                        setLocalesList([defaultEN, ...dbLocales]);
                        setCurrentLocale(defaultEN);
                        dbLocales.forEach((locale) => {
                            if (locale.csvFile) {
                                Papa.parse(locale.csvFile, {
                                    download: true, header: true, skipEmptyLines: true,
                                    complete: (results) => {
                                        const translationObj = {};
                                        results.data.forEach(row => {
                                            const k = row.key ? row.key.trim() : null;
                                            const v = row.value ? row.value.trim() : null;
                                            if (k && v) translationObj[k] = v;
                                        });
                                        i18n.addResourceBundle(locale.code, 'translation', translationObj, true, true);
                                    }
                                });
                            }
                        });
                    } else {
                        setLocalesList([defaultEN]);
                        setCurrentLocale(defaultEN);
                    }
                }

                const resOpts = await getStrapOptions();
                setStrapOptionsList(resOpts?.options || resOpts?.Options || []);

                let res = await getAllLeathers();
                if (res && res.Leathers && res.Leathers.length > 0) {
                    const availableLeathers = res.Leathers.filter(leather =>
                        !isFreeMoveBought(leather) && leather.isHidden !== true
                    );
                    const allHidden = res.Leathers.every(leather => leather.isHidden === true);
                    setHides(allHidden ? [] : availableLeathers);

                    if (!allHidden && availableLeathers.length > 0) {
                        const sessionIdxStr = sessionStorage.getItem("savedHideIndex");
                        let targetIndex = sessionIdxStr ? parseInt(sessionIdxStr, 10) : 0;
                        if (targetIndex >= availableLeathers.length) targetIndex = 0;

                        const firstImageObj = new Image();
                        firstImageObj.crossOrigin = "anonymous";
                        const firstImg = availableLeathers[targetIndex].image;
                        firstImageObj.src = firstImg?.startsWith('http') ? firstImg : `${apiURL}/uploads/leathers/${firstImg}`;
                        firstImageObj.onload = () => setIsLoadingApp(false);
                        firstImageObj.onerror = () => setIsLoadingApp(false);
                    } else { setIsLoadingApp(false); }
                } else {
                    setIsLoadingApp(false);
                }
            } catch (error) { console.error(error); setIsLoadingApp(false); }
        };
        fetchInitialData();
    }, [i18n]);

    return {
        hides, setHides,
        strapOptionsList, setStrapOptionsList,
        localesList, setLocalesList,
        currentLocale, setCurrentLocale,
        localCartAreas, setLocalCartAreas,
        isLoadingApp,
    };
}
